/**
 * Username share-link codec.
 *
 * Web port of `kaether_for_ios/Stoat/E2EE/UsernameShareLink.swift`.
 *
 * A user shares their handle as a tap-to-add URL or a QR code. Anyone
 * scanning/tapping starts a chat without ever seeing the email/phone
 * behind the account. The link carries the username and a 16-byte
 * truncated SHA-256 fingerprint of the owner's identity public key, so
 * the receiver can refuse to proceed if the username's claim (fetched
 * from the server) is bound to a different identity key than the link
 * promised — that blocks a hostile server from rebinding "alice.0000"
 * to Bob's account at link-redeem time.
 *
 * Threat model defences:
 *   1. No email/phone in the link — only username + fingerprint.
 *   2. Universal-link host is a compile-time allowlist of exactly one
 *      domain. Receivers refuse links from typo-squatted hosts.
 *   3. The fingerprint is the only authoritative bit on the wire; the
 *      verifier in `UsernameShareLinkVerifier` checks the fetched
 *      claim's identity key reduces to the same fingerprint, and only
 *      then trusts the claim's signature.
 *
 * Wire formats (byte-identical with iOS):
 *
 *   Universal link:   https://kaether.fr/u/<username>?fp=<base32>
 *   Custom scheme:    kaether:user:invite:v1:<base64url(JSON)>
 *
 * The JSON payload for the custom-scheme form mirrors iOS `UsernameSharePayload`:
 *   { "schema": 1,
 *     "username": { "canonicalName": "alice", "discriminator": 1234 },
 *     "identityKeyFingerprint": "<standard-base64-16-bytes>",
 *     "createdAt": 1700000000 }
 *
 * `identityKeyFingerprint` is encoded by JSON.stringify as standard base64
 * (not base64url), matching Foundation's default Data handling in iOS.
 */

// ---------------------------------------------------------------------------
// Compile-time constants
// ---------------------------------------------------------------------------

/**
 * Compile-time host allowlist. The codec NEVER accepts other hosts — a server
 * cannot surface "kaether.example.com/u/..." and have a receiver treat it as
 * the same brand. Hardcoded string: never read from config.
 */
export const UNIVERSAL_LINK_HOST = "kaether.fr" as const;

export const UNIVERSAL_LINK_PATH_PREFIX = "/u/" as const;

/**
 * Prefix for the custom-scheme form. NEVER "stoat:...".
 */
export const CUSTOM_SCHEME_PREFIX = "kaether:user:invite:v1:" as const;

/**
 * Truncated SHA-256 output length. 128 bits is enough to make preimage /
 * collision attacks against the link binding computationally unreasonable
 * while keeping the URL short.
 */
export const FINGERPRINT_BYTE_COUNT = 16 as const;

export const CURRENT_SCHEMA = 1 as const;

// ---------------------------------------------------------------------------
// Value types
// ---------------------------------------------------------------------------

export interface UsernameHandle {
  /** Validated canonical name (lowercase, a-z0-9_). */
  readonly canonicalName: string;
  /** 4-digit discriminator 0–9999. */
  readonly discriminator: number;
}

/**
 * Everything carried inside both link forms. Mirrors iOS `UsernameSharePayload`.
 */
export interface UsernameShareLink {
  readonly schema: number;
  /** Parsed username (canonicalName + discriminator). */
  readonly handle: UsernameHandle;
  /** 16-byte truncated SHA-256 of the identity public key. */
  readonly fingerprint: Uint8Array;
  /**
   * Unix timestamp (seconds). Universal-link form does not carry this — the
   * decoder fills 0; treat 0 as "unspecified". The custom-scheme form
   * preserves the value.
   */
  readonly createdAt: number;
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

export type UsernameShareLinkErrorKind =
  | "malformed"
  | "unsupportedSchema"
  | "invalidFingerprintLength"
  | "unrecognisedHost"
  | "missingFingerprintParam"
  | "missingUsername";

export class UsernameShareLinkError extends Error {
  constructor(
    public readonly kind: UsernameShareLinkErrorKind,
    detail?: string | number,
  ) {
    super(detail !== undefined ? `${kind}: ${detail}` : kind);
  }
}

// ---------------------------------------------------------------------------
// Handle parsing
// ---------------------------------------------------------------------------

/** Format: `<canonicalName>.<discriminator>` e.g. "alice.0042" */
export function parseHandle(formatted: string): UsernameHandle | null {
  // Find the last dot — the discriminator is to the right of it.
  const lastDot = formatted.lastIndexOf(".");
  if (lastDot <= 0) return null;
  const canonicalName = formatted.slice(0, lastDot);
  const discriminatorStr = formatted.slice(lastDot + 1);
  // canonicalName: non-empty, a-z0-9_ only
  if (!/^[a-z0-9_]{1,32}$/.test(canonicalName)) return null;
  // discriminator: exactly 4 characters, all digits
  if (!/^\d{4}$/.test(discriminatorStr)) return null;
  const discriminator = parseInt(discriminatorStr, 10);
  if (discriminator < 0 || discriminator > 9999) return null;
  return { canonicalName, discriminator };
}

/** Returns the formatted handle string, e.g. "alice.0042". */
export function formatHandle(handle: UsernameHandle): string {
  return `${handle.canonicalName}.${String(handle.discriminator).padStart(4, "0")}`;
}

// ---------------------------------------------------------------------------
// isShareLink — cheap prefix check, no structural validation
// ---------------------------------------------------------------------------

/**
 * Returns true iff the string looks like a username share link (either form).
 * Does NOT validate the payload. Use `decode` / `tryDecode` for that.
 */
export function isShareLink(s: string): boolean {
  if (s.startsWith(CUSTOM_SCHEME_PREFIX)) return true;
  const lower = s.toLowerCase();
  if (
    lower.startsWith(
      `https://${UNIVERSAL_LINK_HOST}${UNIVERSAL_LINK_PATH_PREFIX}`,
    )
  )
    return true;
  return false;
}

// ---------------------------------------------------------------------------
// Encoding
// ---------------------------------------------------------------------------

/**
 * Universal-link form: `https://kaether.fr/u/<handle>?fp=<base32>`.
 * Base32 is RFC 4648, uppercase, no padding (26 chars for 16 bytes).
 */
export function encodeUniversalLink(link: UsernameShareLink): string {
  const fp = base32Encode(link.fingerprint);
  return `https://${UNIVERSAL_LINK_HOST}${UNIVERSAL_LINK_PATH_PREFIX}${formatHandle(link.handle)}?fp=${fp}`;
}

/**
 * Custom-scheme form: `kaether:user:invite:v1:<base64url(JSON)>`.
 * JSON payload mirrors iOS `UsernameSharePayload` exactly (including
 * `identityKeyFingerprint` as standard base64, matching Foundation's
 * default Data JSON encoding).
 */
export function encodeCustomScheme(link: UsernameShareLink): string {
  const json = JSON.stringify({
    schema: link.schema,
    username: {
      canonicalName: link.handle.canonicalName,
      discriminator: link.handle.discriminator,
    },
    identityKeyFingerprint: uint8ArrayToBase64(link.fingerprint),
    createdAt: link.createdAt,
  });
  return CUSTOM_SCHEME_PREFIX + base64UrlEncode(new TextEncoder().encode(json));
}

// ---------------------------------------------------------------------------
// Decoding
// ---------------------------------------------------------------------------

/**
 * Accepts either link form. Throws `UsernameShareLinkError` on any problem.
 * Compile-time host allowlist: only `kaether.fr` is accepted (case-insensitive).
 * Subdomains and similar-looking domains are rejected.
 */
export function decode(s: string): UsernameShareLink {
  if (s.startsWith(CUSTOM_SCHEME_PREFIX)) {
    return decodeCustomScheme(s);
  }
  // Universal-link path (https only)
  if (s.toLowerCase().startsWith("https://")) {
    return decodeUniversalLink(s);
  }
  throw new UsernameShareLinkError(
    "malformed",
    "does not match any share-link shape",
  );
}

/**
 * Returns null for anything that is not a share link or is structurally
 * invalid. Use when probing arbitrary scanned text.
 */
export function tryDecode(s: string): UsernameShareLink | null {
  if (!isShareLink(s)) return null;
  try {
    return decode(s);
  } catch {
    return null;
  }
}

/**
 * Compute the 16-byte fingerprint from an identity public key.
 * First 16 bytes of SHA-256(identityPublicKey).
 * Mirrors iOS `UsernameShareLinkCodec.computeFingerprint`.
 */
export async function computeFingerprint(
  identityPublicKey: Uint8Array,
): Promise<Uint8Array> {
  const hash = await crypto.subtle.digest("SHA-256", identityPublicKey);
  return new Uint8Array(hash).slice(0, FINGERPRINT_BYTE_COUNT);
}

// ---------------------------------------------------------------------------
// Per-form decoders (private)
// ---------------------------------------------------------------------------

function decodeUniversalLink(s: string): UsernameShareLink {
  let url: URL;
  try {
    url = new URL(s);
  } catch {
    throw new UsernameShareLinkError("malformed", "URL parse failed");
  }

  // Scheme check — only https
  if (url.protocol !== "https:") {
    throw new UsernameShareLinkError("malformed", "non-https universal link");
  }

  // Host allow-list: case-insensitive, exact match only.
  // "kaether.fr.evil.com" has host "kaether.fr.evil.com" — not equal.
  // "sub.kaether.fr" has host "sub.kaether.fr" — not equal.
  if (url.hostname.toLowerCase() !== UNIVERSAL_LINK_HOST) {
    throw new UsernameShareLinkError("unrecognisedHost");
  }

  // Path prefix
  if (!url.pathname.startsWith(UNIVERSAL_LINK_PATH_PREFIX)) {
    throw new UsernameShareLinkError(
      "malformed",
      `missing path prefix ${UNIVERSAL_LINK_PATH_PREFIX}`,
    );
  }

  const rawHandle = decodeURIComponent(
    url.pathname.slice(UNIVERSAL_LINK_PATH_PREFIX.length),
  );
  if (!rawHandle) {
    throw new UsernameShareLinkError("missingUsername");
  }

  const handle = parseHandle(rawHandle);
  if (!handle) {
    throw new UsernameShareLinkError("malformed", `unparseable handle: ${rawHandle}`);
  }

  // fp parameter — case-insensitive param name
  let fpEncoded: string | null = null;
  url.searchParams.forEach((value, key) => {
    if (key.toLowerCase() === "fp" && fpEncoded === null) {
      fpEncoded = value;
    }
  });

  if (!fpEncoded) {
    throw new UsernameShareLinkError("missingFingerprintParam");
  }

  const fp = base32Decode(fpEncoded);
  if (fp === null) {
    throw new UsernameShareLinkError("malformed", "invalid base32 in fp param");
  }
  if (fp.length !== FINGERPRINT_BYTE_COUNT) {
    throw new UsernameShareLinkError("invalidFingerprintLength", fp.length);
  }

  return {
    schema: CURRENT_SCHEMA,
    handle,
    fingerprint: fp,
    // Universal links don't carry createdAt — receivers treat 0 as "unspecified".
    createdAt: 0,
  };
}

function decodeCustomScheme(s: string): UsernameShareLink {
  const b64Url = s.slice(CUSTOM_SCHEME_PREFIX.length);
  const jsonBytes = base64UrlDecode(b64Url);
  if (jsonBytes === null) {
    throw new UsernameShareLinkError(
      "malformed",
      "invalid base64url in custom-scheme link",
    );
  }

  let payload: {
    schema: unknown;
    username: unknown;
    identityKeyFingerprint: unknown;
    createdAt: unknown;
  };
  try {
    payload = JSON.parse(new TextDecoder().decode(jsonBytes));
  } catch (e) {
    throw new UsernameShareLinkError("malformed", `json decode failed: ${e}`);
  }

  if (typeof payload.schema !== "number") {
    throw new UsernameShareLinkError("malformed", "schema must be a number");
  }
  if (payload.schema !== CURRENT_SCHEMA) {
    throw new UsernameShareLinkError("unsupportedSchema", payload.schema);
  }

  if (
    typeof payload.username !== "object" ||
    payload.username === null ||
    typeof (payload.username as Record<string, unknown>).canonicalName !==
      "string" ||
    typeof (payload.username as Record<string, unknown>).discriminator !==
      "number"
  ) {
    throw new UsernameShareLinkError("malformed", "invalid username field");
  }

  const usernameRaw = payload.username as {
    canonicalName: string;
    discriminator: number;
  };
  const handle = parseHandle(
    `${usernameRaw.canonicalName}.${String(usernameRaw.discriminator).padStart(4, "0")}`,
  );
  if (!handle) {
    throw new UsernameShareLinkError(
      "malformed",
      `invalid username: ${usernameRaw.canonicalName}.${usernameRaw.discriminator}`,
    );
  }

  if (typeof payload.identityKeyFingerprint !== "string") {
    throw new UsernameShareLinkError(
      "malformed",
      "identityKeyFingerprint must be a string",
    );
  }

  const fpBytes = base64ToUint8Array(payload.identityKeyFingerprint);
  if (fpBytes === null) {
    throw new UsernameShareLinkError(
      "malformed",
      "invalid base64 in identityKeyFingerprint",
    );
  }
  if (fpBytes.length !== FINGERPRINT_BYTE_COUNT) {
    throw new UsernameShareLinkError("invalidFingerprintLength", fpBytes.length);
  }

  const createdAt =
    typeof payload.createdAt === "number" ? payload.createdAt : 0;

  return {
    schema: CURRENT_SCHEMA,
    handle,
    fingerprint: fpBytes,
    createdAt,
  };
}

// ---------------------------------------------------------------------------
// Base64url helpers (wire-compatible with iOS UsernameShareBase64Url)
// ---------------------------------------------------------------------------

export function base64UrlEncode(data: Uint8Array): string {
  // btoa requires a binary string
  let binary = "";
  for (const byte of data) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=/g, "");
}

export function base64UrlDecode(s: string): Uint8Array | null {
  try {
    let padded = s.replace(/-/g, "+").replace(/_/g, "/");
    const pad = (4 - (padded.length % 4)) % 4;
    padded += "=".repeat(pad);
    const binary = atob(padded);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Standard base64 helpers — for identityKeyFingerprint in JSON payload
// (iOS uses Foundation's default Data encoding which is standard base64)
// ---------------------------------------------------------------------------

function uint8ArrayToBase64(data: Uint8Array): string {
  let binary = "";
  for (const byte of data) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary);
}

function base64ToUint8Array(s: string): Uint8Array | null {
  try {
    const binary = atob(s);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// RFC 4648 base32 (no padding) — wire-compatible with iOS UsernameShareBase32
//
// Alphabet: ABCDEFGHIJKLMNOPQRSTUVWXYZ234567 (standard RFC 4648).
// Encoding: uppercase on the wire. Decoding: case-insensitive.
// ---------------------------------------------------------------------------

const BASE32_ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ234567";

export function base32Encode(data: Uint8Array): string {
  let out = "";
  let buffer = 0;
  let bitsLeft = 0;

  for (const byte of data) {
    buffer = (buffer << 8) | byte;
    bitsLeft += 8;
    while (bitsLeft >= 5) {
      const idx = (buffer >>> (bitsLeft - 5)) & 0x1f;
      out += BASE32_ALPHABET[idx];
      bitsLeft -= 5;
    }
  }

  if (bitsLeft > 0) {
    const idx = (buffer << (5 - bitsLeft)) & 0x1f;
    out += BASE32_ALPHABET[idx];
  }

  return out;
}

export function base32Decode(s: string): Uint8Array | null {
  // Build index map
  const indexMap = new Map<string, number>();
  for (let i = 0; i < BASE32_ALPHABET.length; i++) {
    indexMap.set(BASE32_ALPHABET[i], i);
  }

  const upper = s.toUpperCase();
  let buffer = 0;
  let bitsLeft = 0;
  const out: number[] = [];

  for (const ch of upper) {
    const val = indexMap.get(ch);
    if (val === undefined) return null; // non-alphabet character
    buffer = (buffer << 5) | val;
    bitsLeft += 5;
    if (bitsLeft >= 8) {
      out.push((buffer >>> (bitsLeft - 8)) & 0xff);
      bitsLeft -= 8;
    }
  }

  // Trailing bits must be zero — non-zero means tampered input
  if (bitsLeft > 0) {
    const tail = buffer & ((1 << bitsLeft) - 1);
    if (tail !== 0) return null;
  }

  return new Uint8Array(out);
}
